import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import type { IconName } from '@molecule/app-icons'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { type CSSProperties as MolCSSProperties, getClassMap } from '@molecule/app-ui'
import { Button, Flex, Icon, Switch, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import {
  type CacheGetData,
  type CacheKeysData,
  type CacheStatsData,
  errorKeyOf,
  FILE_PREVIEW_CHAR_LIMIT,
  FILE_PREVIEW_MAX_BYTES,
  formatByteSize,
  isBinaryPreviewPath,
  isInsufficientRoleError,
  type ObjectsListData,
  probeStorage,
  type SearchIndexesData,
  type SearchIndexInfo,
  type SearchQueryData,
  serverErrorMessage,
  type StorageProbeFailure,
} from '../utilities/ops-utilities.js'
import { loadServicesState } from '../utilities/services-utilities.js'
import { ACTIVE_PILL, DatabasePanel, INACTIVE_PILL } from './DatabasePanel.js'
import {
  allSecretsManaged,
  Chip,
  dedupeIntegrations,
  EnvRow,
  IntegrationSecrets,
  isUserSetSecret,
  saveIntegrationSecrets,
  SectionHeading,
  type SetupIntegration,
  type SetupResponse,
} from './EnvironmentPanel.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { MutedNote, PanelNotice } from './PanelNotice.js'

/**
 * The Storage panel — every storage type the app can use (database, file
 * storage, cache, search) in ONE sectioned view mirroring the Environment
 * panel's idiom: the same SectionHeading / EnvRow / Chip / IntegrationSecrets
 * pieces (imported from EnvironmentPanel, never duplicated), a single-open
 * accordion, and a visibility-aware 10s `/setup` + `/services` poll.
 *
 *  1. **In your app** — storage types matched by an installed setup
 *     integration's registry category OR by a live installed `@molecule/*`
 *     package (the `GET /services` `packages` list — this is what sees the
 *     ZERO-CONFIG providers `/setup` can't, e.g. memory cache / filesystem
 *     uploads / Postgres search, which declare no secrets), PLUS the database
 *     always (molecule provisions Postgres for every project regardless).
 *     Types molecule handles automatically read "Auto-configured" on the
 *     summary chip, with ONE muted managed line in the expanded details plus
 *     a right-aligned "Use your own provider" switch (the Environment panel's
 *     switch idiom) that reveals the credential inputs only while it is on —
 *     molecule-managed rows never show inputs. The database row embeds the
 *     FULL database explorer (connection card, tables browser, read-only SQL
 *     console); other in-app types render their integrations' rich credential
 *     rows (the configure surface) per the switch.
 *  2. **More storage types** — the rest as discovery rows: the type's
 *     description plus a hint that Synthase installs and wires it from chat.
 *
 * Configure + explore in one place: credentials save through the same
 * `PUT /env` path the Environment panel uses ({@link saveIntegrationSecrets});
 * the database explorer is the embedded {@link DatabasePanel}. Every other
 * row embeds a SIMPLE explorer of its own (the user's "quickly query whatever
 * storage" surface):
 *
 *  - **File storage** — a dev files browser over the existing
 *    `GET /projects/:id/files` endpoints (the app's `api/uploads` dir, with
 *    bounded text previews), plus an on-demand S3 bucket listing via the
 *    `objects:list` storage probe.
 *  - **Cache** — Redis stats / SCAN-bounded key listing / type-aware key
 *    reads via the `cache:*` storage probes.
 *  - **Search** — index listing + a 10-hit query console via the `search:*`
 *    storage probes (Postgres-backed search users are pointed at the
 *    Database row's SQL console instead).
 *
 * All explorers are read-only and LAZY — nothing fires until the row is
 * expanded — and their results live in {@link StoragePanel} state, so they
 * survive the accordion collapsing while the panel stays mounted. The probes
 * hit `POST /projects/:id/storage/probe` (whitelisted server-side; see
 * `probeStorage` in `utilities/ops-utilities.ts`).
 */

// ── The static storage catalog ───────────────────────────────────────────────

/** The stable storage-type ids the panel groups by. */
export type StorageType = 'database' | 'object-storage' | 'cache' | 'search'

/** One storage type in the static catalog. */
export interface StorageCatalogEntry {
  /** Stable id (drives mol-ids + i18n keys). */
  type: StorageType
  /** Bonded icon-set glyph for the row. */
  icon: IconName
  /** Default English label. */
  label: string
  /** The registry categories whose installed packages belong to this type. */
  registryCategories: readonly string[]
  /**
   * `@molecule/*` package-name prefixes that mark this type as present in the
   * app. `/setup` only lists packages that DECLARE secrets, so zero-config
   * providers (memory cache, filesystem uploads, Postgres search) are
   * invisible to it — the live `GET /services` package list joined against
   * these prefixes is what pulls them into "In your app".
   */
  packagePrefixes: readonly string[]
  /** Default English description (the expanded row's first line). */
  description: string
}

/** Every storage type, in display order. */
export const STORAGE_CATALOG: readonly StorageCatalogEntry[] = [
  {
    type: 'database',
    icon: 'database',
    label: 'Database',
    registryCategories: ['database', 'database-remote'],
    packagePrefixes: ['@molecule/api-database'],
    description:
      "Relational database for your app's data — provisioned and hosted by Molecule automatically, in dev and production.",
  },
  {
    type: 'object-storage',
    icon: 'folder',
    label: 'File storage',
    registryCategories: ['storage', 'uploads'],
    packagePrefixes: ['@molecule/api-uploads'],
    // Deliberately says nothing about WHERE files land: that depends on the
    // uploads bond the app installed and on whether this deployment
    // provisions managed storage at all. The row's own managed line answers
    // it — a fixed "Local disk in dev" here was simply false for the S3 apps
    // that make up most of the fleet.
    description: 'Uploaded files and objects.',
  },
  {
    type: 'cache',
    icon: 'sync',
    label: 'Cache',
    registryCategories: ['cache'],
    packagePrefixes: ['@molecule/api-cache'],
    description: 'Fast key-value cache — free in dev.',
  },
  {
    type: 'search',
    icon: 'search',
    label: 'Search',
    registryCategories: ['search'],
    packagePrefixes: ['@molecule/api-search'],
    description: 'Full-text search — free in dev.',
  },
]

/**
 * One molecule-managed default-provider variant for a storage type. A variant
 * is selected when its signal matches — `managedVault` (any nested secret is
 * a managed vault row, e.g. molecule's managed object storage), a
 * `packagePrefix` (an installed package identifies the managed provider), or
 * neither (always applies — the molecule-provisioned database). First match
 * in the type's list wins.
 */
interface ManagedDefaultVariant {
  /** i18n suffix under `storage.defaultProvider.*` / `storage.managed.*`. */
  key: string
  /** Selects the variant when any nested secret has `vaultSource: 'managed'`. */
  managedVault?: boolean
  /** Selects the variant when a matching package is installed. */
  packagePrefix?: string
  /**
   * Selects the variant when the app has an api backend at all (any live
   * `@molecule/api-*` package) — for platform defaults that need no provider
   * package signal, e.g. local-disk file storage.
   */
  apiBackend?: boolean
  /**
   * Requires the DEPLOYMENT to provision managed object storage
   * (`/setup` → `platform.managedObjectStorage`). A variant that promises a
   * molecule default must not be selectable where no such default exists.
   */
  managedStorage?: boolean
  /**
   * Selects the variant only when NO provider package is installed for this
   * type — for platform-default variants that describe what happens in the
   * absence of a choice. Without it, the "local disk" default would also
   * describe an app that explicitly installed an S3 bond.
   */
  noProviderPackage?: boolean
  /**
   * This variant describes the USER'S OWN provider, not a molecule-managed
   * one. It explains the row but offers NO "Use your own provider" switch —
   * there is no molecule default to switch back to, and pretending there is
   * produced the row's worst line: "Using your own provider — Molecule's
   * default is on standby" for a user who had configured nothing and for whom
   * no default existed.
   */
  byo?: boolean
  /** Default English "Molecule default: <label>" label. */
  label: string
  /**
   * Default English managed line for the settings row (non-database types) —
   * the "provisioned by molecule" story lives in the expanded details (the
   * summary chip just says "Auto-configured"), MERGED with the provider story
   * into a single muted line so explanations never stack. Shown while the
   * "Use your own provider" switch is off.
   */
  explainer?: string
}

/**
 * Molecule-managed default providers per storage type — what works out of the
 * box with nothing to configure.
 *
 * ONE VARIANT PER PROVIDER, matched on the package the app actually installed.
 * A single catch-all variant per type is how the cache row came to tell every
 * memcached and in-process-memory project that "Redis runs inside your app's
 * container" — and then that no cache was running. First match wins, so the
 * specific prefixes are listed before any catch-all.
 *
 * Object storage additionally depends on the DEPLOYMENT, not just the app:
 * `managedStorage` variants are selected only when `/setup` reports
 * `platform.managedObjectStorage`. Where the platform provisions no buckets,
 * an S3 app's keys are genuinely the user's to supply and the row says so
 * instead of promising a molecule default that does not exist there.
 */
const MOLECULE_DEFAULT_PROVIDERS: Record<StorageType, readonly ManagedDefaultVariant[]> = {
  database: [{ key: 'database', label: 'Managed Postgres' }],
  'object-storage': [
    {
      key: 'object-storage-vault',
      managedVault: true,
      label: 'Molecule object storage',
      explainer:
        'Molecule object storage (S3-compatible) — provisioned automatically, dev and production.',
    },
    {
      key: 'object-storage-filesystem',
      packagePrefix: '@molecule/api-uploads-filesystem',
      label: "Your app's disk",
      explainer:
        "Files are written to your app's own disk. They live as long as the app does — bring object-storage credentials for production.",
    },
    // Managed-storage deployments only: with buckets provisioned for every
    // project, an s3 app really does get a molecule default.
    {
      key: 'object-storage-s3-managed',
      packagePrefix: '@molecule/api-uploads-s3',
      managedStorage: true,
      label: 'Molecule object storage',
      explainer:
        'Molecule object storage (S3-compatible) — provisioned automatically, dev and production.',
    },
    // …and where it does NOT, the same app's bucket is genuinely the user's to
    // bring. Says so, and offers no switch, because there is nothing to switch
    // back to.
    {
      key: 'object-storage-s3-byo',
      packagePrefix: '@molecule/api-uploads-s3',
      byo: true,
      label: 'S3-compatible storage',
      explainer:
        'This app stores files in S3-compatible object storage. Add your own bucket credentials below.',
    },
    // The platform default when the app has an api backend and no uploads
    // provider package at all.
    {
      key: 'object-storage-local',
      apiBackend: true,
      noProviderPackage: true,
      label: 'Local disk (dev)',
      explainer: "Managed by Molecule — files are stored on your app's disk in dev.",
    },
  ],
  cache: [
    {
      key: 'cache-redis',
      packagePrefix: '@molecule/api-cache-redis',
      label: 'Redis (in-container)',
      explainer:
        "Redis runs inside your app's container — dev and production, managed by Molecule.",
    },
    {
      key: 'cache-memcached',
      packagePrefix: '@molecule/api-cache-memcached',
      label: 'Memcached (in-container)',
      explainer:
        "Memcached runs inside your app's container — dev and production, managed by Molecule.",
    },
    {
      key: 'cache-memory',
      packagePrefix: '@molecule/api-cache-memory',
      label: 'In-process memory',
      explainer:
        'The cache lives inside your app process — nothing to run, and it empties on every restart.',
    },
  ],
  search: [
    {
      key: 'search-meilisearch',
      packagePrefix: '@molecule/api-search-meilisearch',
      label: 'Meilisearch (in-container)',
      explainer:
        "Meilisearch runs inside your app's container — dev and production, managed by Molecule.",
    },
    {
      key: 'search',
      packagePrefix: '@molecule/api-search-postgres',
      label: 'Postgres-backed',
      explainer:
        'Managed by Molecule — search runs on your provisioned Postgres, no extra server needed.',
    },
    {
      key: 'search-typesense',
      packagePrefix: '@molecule/api-search-typesense',
      byo: true,
      label: 'Typesense',
      explainer: 'Search runs on your own Typesense cluster.',
    },
    {
      key: 'search-elasticsearch',
      packagePrefix: '@molecule/api-search-elasticsearch',
      byo: true,
      label: 'Elasticsearch',
      explainer: 'Search runs on your own Elasticsearch cluster.',
    },
  ],
}

/** The cache providers the panel can describe, derived from installed packages. */
type CacheProvider = 'redis' | 'memcached' | 'memory'

/** The search providers the panel can describe, derived from installed packages. */
type SearchProvider = 'meilisearch' | 'postgres' | 'typesense' | 'elasticsearch'

/**
 * Installed-package prefix → the provider it identifies. The panel labels
 * every storage row from the package the app really has, so a new provider
 * bond is one row here rather than a special case in the copy.
 */
const CACHE_PROVIDER_PACKAGES: ReadonlyArray<readonly [string, CacheProvider]> = [
  ['@molecule/api-cache-redis', 'redis'],
  ['@molecule/api-cache-memcached', 'memcached'],
  ['@molecule/api-cache-memory', 'memory'],
]

/** Installed-package prefix → search provider (see {@link CACHE_PROVIDER_PACKAGES}). */
const SEARCH_PROVIDER_PACKAGES: ReadonlyArray<readonly [string, SearchProvider]> = [
  ['@molecule/api-search-meilisearch', 'meilisearch'],
  ['@molecule/api-search-postgres', 'postgres'],
  ['@molecule/api-search-typesense', 'typesense'],
  ['@molecule/api-search-elasticsearch', 'elasticsearch'],
]

/** Display names for the providers a probe can report. */
const PROVIDER_LABELS: Record<string, string> = {
  redis: 'Redis',
  memcached: 'Memcached',
  memory: 'In-process memory',
  meilisearch: 'Meilisearch',
  postgres: 'Postgres',
  typesense: 'Typesense',
  elasticsearch: 'Elasticsearch',
}

/**
 * The provider a package list identifies, or null when none matches.
 * @param packages - The app's installed `@molecule/*` packages.
 * @param table - The prefix → provider table for this storage type.
 * @returns The identified provider, or null.
 */
function providerFromPackages<T extends string>(
  packages: readonly string[],
  table: ReadonlyArray<readonly [string, T]>,
): T | null {
  for (const [prefix, provider] of table) {
    if (packages.some((pkg) => pkg.startsWith(prefix))) return provider
  }
  return null
}

/**
 * The per-type example prompt in the ask-Synthase swap hint (shown when "Use
 * your own provider" is switched on for a type with no credentialed provider
 * installed).
 */
const SWAP_EXAMPLES: Record<Exclude<StorageType, 'database'>, string> = {
  cache: 'use Redis for caching',
  'object-storage': 'use S3 for file storage',
  search: 'use Meilisearch for search',
}

// ── Explorer shared pieces ───────────────────────────────────────────────────

/** The explorer input idiom (EnvVarEditor's Add row: input flex:1 + button). */
const EXPLORER_INPUT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 32,
  boxSizing: 'border-box',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 12,
  fontFamily: 'var(--mol-font-mono, monospace)',
  color: 'var(--mol-color-text, #e0e0e0)',
  outline: 'none',
}

/**
 * The right-aligned button that terminates an explorer input row. Typed as the
 * molecule (`@molecule/app-ui`) CSSProperties since it is only ever passed to
 * the bonded `<Button>`, whose `style` prop uses that framework-agnostic type.
 */
const EXPLORER_BUTTON: MolCSSProperties = { flexShrink: 0, height: 32 }

/**
 * Whether this viewport gets the taller touch-sized explorer controls. Both the
 * input and its trailing button read the SAME predicate, so the row can never
 * grow one without the other.
 *
 * @returns True on phones or any coarse pointer.
 */
function useExplorerTouchSizing(): boolean {
  const isMobile = useIsMobile()
  const coarse = useMediaQuery('(pointer: coarse)')
  return isMobile || coarse
}

/**
 * {@link EXPLORER_INPUT} adjusted for the viewport: 16px font on phones/touch
 * (iOS Safari zooms the page when a focusable field below 16px receives
 * focus) with a matching 40px height; the desktop style verbatim otherwise.
 * @returns The explorer input style for the current viewport.
 */
function useExplorerInputStyle(): React.CSSProperties {
  return useExplorerTouchSizing() ? { ...EXPLORER_INPUT, fontSize: 16, height: 40 } : EXPLORER_INPUT
}

/**
 * {@link EXPLORER_BUTTON} at the height of the input it shares a row with. The
 * constant is a fixed 32, which left every explorer row's trailing button 8px
 * short of its 40px input on phones/touch.
 *
 * @returns The explorer button style for the current viewport.
 */
function useExplorerButtonStyle(): MolCSSProperties {
  return useExplorerTouchSizing() ? { ...EXPLORER_BUTTON, height: 40 } : EXPLORER_BUTTON
}

/** Bounded monospace results block (the database results-grid idiom). */
const EXPLORER_PRE: React.CSSProperties = {
  margin: 0,
  padding: 10,
  overflow: 'auto',
  maxHeight: 240,
  fontSize: 12,
  fontFamily: 'var(--mol-font-mono, monospace)',
  lineHeight: 1.5,
  color: 'var(--color-foreground)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

/**
 * Uppercase sub-heading inside an explorer (the DatabasePanel "Tables" /
 * "Read-only SQL console" idiom).
 * @param root0 - Component props.
 * @param root0.children - Heading text.
 * @param root0.style - Optional style overrides (e.g. `margin: 0` in a flex row).
 * @returns The rendered heading.
 */
function ExplorerHeading({
  children,
  style,
}: {
  children: ReactNode
  style?: React.CSSProperties
}) {
  return (
    <h4
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: 'var(--mol-color-text-secondary, #aaaaaa)',
        margin: '0 0 8px',
        ...style,
      }}
    >
      {children}
    </h4>
  )
}

/**
 * A probe/explorer note: expected states (`tone="hint"`, e.g. notConfigured)
 * are plain {@link MutedNote} lines — boxes are for attention, plain lines are
 * for explanation — while the sandbox-not-running state keeps the
 * {@link PanelNotice} warning box and real failures keep the error box
 * carrying the (monospace) server message.
 * @param root0 - Component props.
 * @param root0.molId - Automation id.
 * @param root0.tone - Hint (expected state) vs warning (sandbox down) vs error (real failure).
 * @param root0.children - The message.
 * @returns The rendered note.
 */
function ProbeNote({
  molId,
  tone,
  children,
}: {
  molId: string
  tone: 'hint' | 'warning' | 'error'
  children: ReactNode
}) {
  if (tone === 'error') {
    return (
      <PanelNotice tone="error" molId={molId}>
        <span
          style={{
            fontSize: 12,
            fontFamily: 'var(--mol-font-mono, monospace)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {children}
        </span>
      </PanelNotice>
    )
  }
  if (tone === 'warning') {
    return (
      <PanelNotice tone="warning" molId={molId}>
        {children}
      </PanelNotice>
    )
  }
  return <MutedNote molId={molId}>{children}</MutedNote>
}

/**
 * Renders a probe failure with the tone its SITUATION deserves, because they
 * are genuinely different situations:
 *
 *  - `forbidden` — a deliberate role denial. An informational notice naming
 *    the role needed, never the raw server string "Access denied" in a red
 *    monospace box (which is exactly what a team editor used to get, with no
 *    click, on first expanding a row).
 *  - `sandboxNotRunning` — a warning with the next step (start it).
 *  - `notConfigured` — expected, quiet hint.
 *  - `serverDown` — this app HAS the provider and it is not answering: a real
 *    error, and the one case where the server's own message is the useful
 *    part. Presenting it as `notConfigured` is how a platform outage got
 *    explained to users as normal configuration.
 *  - anything else — a real error carrying the server message.
 *
 * @param root0 - Component props.
 * @param root0.molId - Automation id for the note.
 * @param root0.failure - The failed probe result.
 * @param root0.notConfiguredHint - Friendly per-explorer copy for the notConfigured state.
 * @returns The rendered note.
 */
function ProbeFailureNote({
  molId,
  failure,
  notConfiguredHint,
}: {
  molId: string
  failure: StorageProbeFailure
  notConfiguredHint: string
}) {
  const { t } = useTranslation()
  if (failure.forbidden) {
    return (
      <ProbeNote molId={molId} tone="hint">
        {t('storage.forbidden', undefined, {
          defaultValue: 'Exploring stored data needs an admin role on this project.',
        })}
      </ProbeNote>
    )
  }
  if (failure.sandboxNotRunning) {
    return (
      <ProbeNote molId={molId} tone="warning">
        {t('storage.sandboxNotRunning', undefined, {
          defaultValue: 'The sandbox is not running — start it to explore storage.',
        })}
      </ProbeNote>
    )
  }
  if (failure.unsupported) {
    return (
      <ProbeNote molId={molId} tone="hint">
        {failure.error ||
          t('storage.unsupported', undefined, {
            defaultValue: "This provider doesn't support that.",
          })}
      </ProbeNote>
    )
  }
  if (failure.serverDown) {
    // The app HAS this provider and its server did not answer. A warning with
    // the next step, not a red box holding a raw message: the user can restart
    // the app, and there is nothing for them to configure.
    return (
      <ProbeNote molId={molId} tone="warning">
        {t(
          'storage.serverDown',
          { message: failure.error },
          {
            defaultValue: `${failure.error || 'The server is not answering'} — restart your app to bring it back.`,
          },
        )}
      </ProbeNote>
    )
  }
  if (failure.notConfigured) {
    return (
      <ProbeNote molId={molId} tone="hint">
        {notConfiguredHint}
      </ProbeNote>
    )
  }
  return (
    <ProbeNote molId={molId} tone="error">
      {failure.error ||
        t('storage.probeFailed', undefined, { defaultValue: 'The storage probe failed to run.' })}
    </ProbeNote>
  )
}

/**
 * True when a failure should REPLACE the explorer UI — the states where there
 * is nothing to explore (not configured, sandbox down, no access). A
 * `serverDown` failure keeps the explorer mounted: the controls are still
 * meaningful once the server comes back.
 */
function hidesExplorer(failure: StorageProbeFailure | null): failure is StorageProbeFailure {
  return (
    !!failure && (!!failure.notConfigured || !!failure.sandboxNotRunning || !!failure.forbidden)
  )
}

// ── Cache explorer ───────────────────────────────────────────────────────────

/** The cache row's kept-while-mounted explorer state. */
interface CacheRowState {
  /** Whether the lazy on-expand stats probe has completed (success or failure). */
  loaded: boolean
  /** Which probe is in flight (null = idle). */
  busy: 'stats' | 'keys' | 'get' | null
  stats: CacheStatsData | null
  /** The most recent probe failure (stats/keys/get share one slot). */
  failure: StorageProbeFailure | null
  /** The key-pattern draft (SCAN MATCH syntax; defaults to `*`). */
  pattern: string
  keys: string[] | null
  keysTruncated: boolean
  /** The last fetched key value (also marks the selected key). */
  value: CacheGetData | null
}

/** Initial {@link CacheRowState}. */
const INITIAL_CACHE_STATE: CacheRowState = {
  loaded: false,
  busy: null,
  stats: null,
  failure: null,
  pattern: '*',
  keys: null,
  keysTruncated: false,
  value: null,
}

/**
 * The cache row's explorer: a compact stats line (fired lazily on expand), a
 * pattern + "List keys" row, a clickable key-pill list, and a type-aware value
 * view. All read-only `cache:*` probes.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose cache to probe.
 * @param root0.state - Lifted row state (survives the accordion collapsing).
 * @param root0.patch - Partial-state merger owned by {@link StoragePanel}.
 * @returns The rendered explorer.
 */
function CacheExplorer({
  projectId,
  provider,
  state,
  patch,
}: {
  projectId: string
  /**
   * The cache provider the app INSTALLED, derived from its package list
   * ('redis' | 'memcached' | 'memory' | null). It decides the empty-state
   * copy, which the probe alone cannot: an in-process memory cache has no
   * server to answer, so "nothing is running" would be both true and useless.
   */
  provider: CacheProvider | null
  state: CacheRowState
  patch: (p: Partial<CacheRowState>) => void
}) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const http = useHttpClient()
  const explorerInput = useExplorerInputStyle()
  const explorerButton = useExplorerButtonStyle()

  // Every probe carries the provider the APP declares. Without it the probe
  // reports on whatever answers a well-known port — which in a sandbox can be
  // a Redis this app never touches, presented as "your cache" directly under a
  // row correctly saying the cache is in-process.
  const providerArg = useMemo(() => (provider ? { provider } : undefined), [provider])

  const loadStats = useCallback(async () => {
    patch({ busy: 'stats', failure: null })
    const res = await probeStorage<CacheStatsData>(http, projectId, 'cache', 'stats', providerArg)
    if (res.ok) patch({ busy: null, loaded: true, stats: res.data })
    else patch({ busy: null, loaded: true, stats: null, failure: res })
  }, [http, projectId, patch, providerArg])

  // Lazy: fires on first expand only; kept results skip the refetch.
  useEffect(() => {
    if (!state.loaded && state.busy === null) void loadStats()
  }, [state.loaded, state.busy, loadStats])

  const listKeys = useCallback(async () => {
    patch({ busy: 'keys', failure: null })
    const res = await probeStorage<CacheKeysData>(http, projectId, 'cache', 'keys', {
      pattern: state.pattern.trim() || '*',
      ...providerArg,
    })
    if (res.ok) patch({ busy: null, keys: res.data.keys, keysTruncated: res.data.truncated })
    else patch({ busy: null, failure: res })
  }, [http, projectId, patch, state.pattern, providerArg])

  const getKey = useCallback(
    async (key: string) => {
      patch({ busy: 'get', failure: null, value: null })
      const res = await probeStorage<CacheGetData>(http, projectId, 'cache', 'get', {
        key,
        ...providerArg,
      })
      if (res.ok) patch({ busy: null, value: res.data })
      else patch({ busy: null, failure: res })
    },
    [http, projectId, patch, providerArg],
  )

  // The empty state depends on WHICH cache the app installed — one sentence
  // for all of them told memcached users Redis was running and memory-cache
  // users that nothing was.
  const notConfiguredHint =
    provider === 'memory'
      ? t('storage.cache.memoryNote', undefined, {
          defaultValue:
            'This app caches inside its own process, so there is no cache server to browse.',
        })
      : provider === 'memcached'
        ? t('storage.cache.memcachedNotRunning', undefined, {
            defaultValue: 'Memcached is not answering — restart your app to bring it back.',
          })
        : provider === 'redis'
          ? t('storage.cache.redisNotRunning', undefined, {
              defaultValue: 'Redis is not answering — restart your app to bring it back.',
            })
          : t('storage.cache.notConfigured', undefined, {
              defaultValue: 'No cache is running yet — ask Synthase in chat to add caching.',
            })

  if (state.busy === 'stats') {
    return (
      <Flex align="center" justify="center" className={cm.sp('py', 3)}>
        <MoleculeSpinner size="sm" />
      </Flex>
    )
  }

  // Expected empty states replace the whole explorer with a quiet hint.
  if (hidesExplorer(state.failure)) {
    return (
      <ProbeFailureNote
        molId="storage-cache-note"
        failure={state.failure}
        notConfiguredHint={notConfiguredHint}
      />
    )
  }

  const statsParts: string[] = []
  if (state.stats) {
    const count = state.stats.dbsize
    statsParts.push(
      t(
        'storage.cache.keyCount',
        { count },
        { defaultValue: `${count} key${count === 1 ? '' : 's'}` },
      ),
    )
    // Name the server that actually answered, not a hardcoded "Redis".
    const version = state.stats.redis_version ?? state.stats.server_version
    if (version) {
      const name = PROVIDER_LABELS[state.stats.provider ?? 'redis'] ?? 'Cache'
      statsParts.push(
        t(
          'storage.cache.version',
          { provider: name, version },
          { defaultValue: `${name} ${version}` },
        ),
      )
    }
    if (state.stats.used_memory_human) {
      statsParts.push(
        t(
          'storage.cache.memory',
          { memory: state.stats.used_memory_human },
          { defaultValue: `${state.stats.used_memory_human} used` },
        ),
      )
    }
  }

  const ttlLabel = (value: CacheGetData): string =>
    typeof value.ttl === 'number' && value.ttl >= 0
      ? t('storage.cache.ttl', { ttl: value.ttl }, { defaultValue: `TTL ${value.ttl}s` })
      : t('storage.cache.noExpiry', undefined, { defaultValue: 'no expiry' })

  return (
    <div data-mol-id="storage-cache-explorer">
      {state.stats && (
        <div
          data-mol-id="storage-cache-stats"
          style={{
            fontSize: 12,
            fontFamily: 'var(--mol-font-mono, monospace)',
            color: 'var(--color-foreground-secondary)',
            marginBottom: 8,
          }}
        >
          {statsParts.join(' · ')}
        </div>
      )}

      {/* pattern + List keys (input flex:1 + right-aligned button) */}
      <Flex align="center" gap="sm" style={{ marginBottom: 8 }}>
        <input
          type="text"
          data-mol-id="storage-cache-pattern"
          value={state.pattern}
          onChange={(e) => patch({ pattern: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && state.busy === null) void listKeys()
          }}
          placeholder={t('storage.cache.patternPlaceholder', undefined, {
            defaultValue: 'Key pattern, e.g. session:*',
          })}
          spellCheck={false}
          style={explorerInput}
        />
        <Button
          size="sm"
          variant="outline"
          loading={state.busy === 'keys'}
          disabled={state.busy !== null}
          onClick={() => void listKeys()}
          data-mol-id="storage-cache-list"
          style={explorerButton}
        >
          {t('storage.cache.listKeys', undefined, { defaultValue: 'List keys' })}
        </Button>
      </Flex>

      {state.failure && (
        <ProbeFailureNote
          molId="storage-cache-note"
          failure={state.failure}
          notConfiguredHint={notConfiguredHint}
        />
      )}

      {state.keys && (
        <>
          {state.keys.length === 0 ? (
            <div
              data-mol-id="storage-cache-keys"
              className={cm.textMuted}
              style={{ fontSize: 12, marginBottom: 8 }}
            >
              {t('storage.cache.noKeys', undefined, {
                defaultValue: 'No keys match this pattern.',
              })}
            </div>
          ) : (
            <Flex
              align="center"
              gap="xs"
              wrap="wrap"
              data-mol-id="storage-cache-keys"
              style={{ marginBottom: 8 }}
            >
              {state.keys.map((key) => {
                const isActive = state.value?.key === key
                return (
                  <button
                    key={key}
                    type="button"
                    data-mol-id={`storage-cache-key-${key}`}
                    onClick={() => void getKey(key)}
                    // mol-bespoke-button: cache-key SELECTOR pill, not a CTA —
                    // one per key in a wrapping monospace list, on the shared
                    // ACTIVE_PILL/INACTIVE_PILL token treatment, with the
                    // compact touch floor added.
                    className={cm.touchTargetCompact}
                    style={{
                      fontSize: 12,
                      padding: '3px 9px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontFamily: 'var(--mol-font-mono, monospace)',
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      ...(isActive ? ACTIVE_PILL : INACTIVE_PILL),
                    }}
                  >
                    {key}
                  </button>
                )
              })}
            </Flex>
          )}
          {state.keysTruncated && (
            <div className={cm.textMuted} style={{ fontSize: 11, marginBottom: 8 }}>
              {t('storage.cache.truncatedKeys', undefined, {
                defaultValue: 'Showing the first 100 keys — narrow the pattern to see more.',
              })}
            </div>
          )}
        </>
      )}

      {state.busy === 'get' && (
        <Flex align="center" justify="center" className={cm.sp('py', 2)}>
          <MoleculeSpinner size="sm" />
        </Flex>
      )}

      {state.value && (
        <div
          data-mol-id="storage-cache-value"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            marginBottom: 8,
            overflow: 'hidden',
          }}
        >
          <Flex
            align="center"
            gap="sm"
            style={{ padding: '6px 10px', borderBottom: '1px solid var(--color-border)' }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'var(--mol-font-mono, monospace)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {state.value.key}
            </span>
            <span
              className={cm.textMuted}
              style={{ fontSize: 11, fontFamily: 'var(--mol-font-mono, monospace)', flexShrink: 0 }}
            >
              {state.value.type === 'none'
                ? t('storage.cache.typeNone', undefined, { defaultValue: 'missing' })
                : `${state.value.type} · ${ttlLabel(state.value)}`}
            </span>
          </Flex>
          {state.value.type === 'none' ? (
            <div className={cm.textMuted} style={{ padding: 10, fontSize: 12 }}>
              {t('storage.cache.missingKey', undefined, {
                defaultValue: 'This key does not exist (it may have expired).',
              })}
            </div>
          ) : state.value.type === 'string' ? (
            <>
              <pre style={EXPLORER_PRE}>{state.value.value ?? ''}</pre>
              {state.value.truncated && (
                <div className={cm.textMuted} style={{ padding: '0 10px 8px', fontSize: 11 }}>
                  {t('storage.cache.valueTruncated', undefined, {
                    defaultValue: 'Value truncated to the first 10 KB.',
                  })}
                </div>
              )}
            </>
          ) : (
            <div
              style={{ padding: 10, fontSize: 12, fontFamily: 'var(--mol-font-mono, monospace)' }}
            >
              {t(
                'storage.cache.entries',
                { type: state.value.type, count: state.value.size ?? 0 },
                { defaultValue: `${state.value.type} · ${state.value.size ?? 0} entries` },
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Search explorer ──────────────────────────────────────────────────────────

/** The search row's kept-while-mounted explorer state. */
interface SearchRowState {
  /** Whether the lazy on-expand indexes probe has completed. */
  loaded: boolean
  busy: 'indexes' | 'query' | null
  provider: string | null
  indexes: SearchIndexInfo[] | null
  failure: StorageProbeFailure | null
  /** The uid of the index queries run against. */
  selectedIndex: string
  query: string
  hits: Record<string, unknown>[] | null
  total: number
}

/** Initial {@link SearchRowState}. */
const INITIAL_SEARCH_STATE: SearchRowState = {
  loaded: false,
  busy: null,
  provider: null,
  indexes: null,
  failure: null,
  selectedIndex: '',
  query: '',
  hits: null,
  total: 0,
}

/**
 * The search row's explorer: index pills (fired lazily on expand) + an index
 * select, query input and "Search" button rendering up to 10 hits as
 * pretty-printed JSON. Postgres-backed search (notConfigured) points at the
 * Database row instead.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose search to probe.
 * @param root0.state - Lifted row state (survives the accordion collapsing).
 * @param root0.patch - Partial-state merger owned by {@link StoragePanel}.
 * @returns The rendered explorer.
 */
function SearchExplorer({
  projectId,
  provider,
  state,
  patch,
}: {
  projectId: string
  /**
   * The search provider the app INSTALLED, from its package list. Postgres
   * search has no server to probe (the Database row IS its explorer), while a
   * Meilisearch app that cannot reach its server has a real problem — the
   * probe's "nothing answered" means opposite things for the two, and one
   * pinned sentence for both told Meilisearch users their search ran on
   * Postgres while the sidecar was down.
   */
  provider: SearchProvider | null
  state: SearchRowState
  patch: (p: Partial<SearchRowState>) => void
}) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const http = useHttpClient()
  const explorerInput = useExplorerInputStyle()
  const explorerButton = useExplorerButtonStyle()

  const loadIndexes = useCallback(async () => {
    patch({ busy: 'indexes', failure: null })
    const res = await probeStorage<SearchIndexesData>(
      http,
      projectId,
      'search',
      'indexes',
      provider ? { provider } : undefined,
    )
    if (res.ok) {
      patch({
        busy: null,
        loaded: true,
        provider: res.data.provider,
        indexes: res.data.indexes,
        // Pre-select the first index so the query row works immediately.
        selectedIndex: state.selectedIndex || res.data.indexes[0]?.uid || '',
      })
    } else {
      patch({ busy: null, loaded: true, indexes: null, failure: res })
    }
  }, [http, projectId, patch, state.selectedIndex, provider])

  // Lazy: fires on first expand only; kept results skip the refetch.
  useEffect(() => {
    if (!state.loaded && state.busy === null) void loadIndexes()
  }, [state.loaded, state.busy, loadIndexes])

  const runSearch = useCallback(async () => {
    if (!state.selectedIndex) return
    patch({ busy: 'query', failure: null })
    const res = await probeStorage<SearchQueryData>(http, projectId, 'search', 'query', {
      index: state.selectedIndex,
      query: state.query,
      ...(provider ? { provider } : {}),
    })
    if (res.ok) patch({ busy: null, hits: res.data.hits, total: res.data.total })
    else patch({ busy: null, failure: res })
  }, [http, projectId, patch, state.selectedIndex, state.query, provider])

  // Postgres-backed search has no separate server — the database row IS the
  // explorer for it. Every OTHER provider needs its own sentence.
  const notConfiguredHint =
    provider === 'postgres'
      ? t('storage.search.postgresFallback', undefined, {
          defaultValue:
            'Your search runs on the project database — query it from the Database row above.',
        })
      : provider === 'meilisearch'
        ? t('storage.search.meilisearchNotRunning', undefined, {
            defaultValue: 'Meilisearch is not answering — restart your app to bring it back.',
          })
        : provider === 'typesense' || provider === 'elasticsearch'
          ? t(
              'storage.search.byoNotReachable',
              { provider: PROVIDER_LABELS[provider] },
              {
                defaultValue: `${PROVIDER_LABELS[provider]} is not reachable — check its URL and credentials above.`,
              },
            )
          : t('storage.search.notConfigured', undefined, {
              defaultValue: 'No search server is running yet — ask Synthase in chat to add search.',
            })

  if (state.busy === 'indexes') {
    return (
      <Flex align="center" justify="center" className={cm.sp('py', 3)}>
        <MoleculeSpinner size="sm" />
      </Flex>
    )
  }

  if (hidesExplorer(state.failure)) {
    return (
      <ProbeFailureNote
        molId="storage-search-note"
        failure={state.failure}
        notConfiguredHint={notConfiguredHint}
      />
    )
  }

  return (
    <div data-mol-id="storage-search-explorer">
      {state.indexes && state.indexes.length === 0 && (
        <div
          data-mol-id="storage-search-indexes"
          className={cm.textMuted}
          style={{ fontSize: 12, marginBottom: 8 }}
        >
          {t('storage.search.noIndexes', undefined, { defaultValue: 'No search indexes yet.' })}
        </div>
      )}

      {state.indexes && state.indexes.length > 0 && (
        <>
          <Flex
            align="center"
            gap="xs"
            wrap="wrap"
            data-mol-id="storage-search-indexes"
            style={{ marginBottom: 8 }}
          >
            {state.indexes.map((index) => {
              const isActive = state.selectedIndex === index.uid
              const meta =
                typeof index.documents === 'number'
                  ? t(
                      'storage.search.docCount',
                      { count: index.documents },
                      { defaultValue: `${index.documents} docs` },
                    )
                  : null
              return (
                <button
                  key={index.uid}
                  type="button"
                  data-mol-id={`storage-search-index-${index.uid}`}
                  aria-pressed={isActive}
                  onClick={() => patch({ selectedIndex: index.uid })}
                  // mol-bespoke-button: search-index SELECTOR pill (aria-pressed),
                  // not a CTA — shared ACTIVE_PILL/INACTIVE_PILL token treatment,
                  // with the compact touch floor added.
                  className={cm.touchTargetCompact}
                  style={{
                    fontSize: 12,
                    padding: '3px 9px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontFamily: 'var(--mol-font-mono, monospace)',
                    ...(isActive ? ACTIVE_PILL : INACTIVE_PILL),
                  }}
                >
                  {index.uid}
                  {meta ? ` · ${meta}` : ''}
                </button>
              )
            })}
          </Flex>

          {/* index select + query input + right-aligned Search button — wraps
              only when the three can't share the line (390px phones). */}
          <Flex align="center" gap="sm" wrap="wrap" style={{ marginBottom: 8 }}>
            <select
              data-mol-id="storage-search-index"
              aria-label={t('storage.search.indexLabel', undefined, {
                defaultValue: 'Search index',
              })}
              value={state.selectedIndex}
              onChange={(e) => patch({ selectedIndex: e.target.value })}
              style={{ ...explorerInput, flex: '0 1 auto', maxWidth: 180 }}
            >
              {state.indexes.map((index) => (
                <option key={index.uid} value={index.uid}>
                  {index.uid}
                </option>
              ))}
            </select>
            <input
              type="text"
              data-mol-id="storage-search-query"
              value={state.query}
              onChange={(e) => patch({ query: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && state.busy === null) void runSearch()
              }}
              placeholder={t('storage.search.queryPlaceholder', undefined, {
                defaultValue: 'Search query…',
              })}
              spellCheck={false}
              // The min width makes the wrap engage instead of the query
              // squeezing to nothing beside the index select.
              style={{ ...explorerInput, minWidth: 140 }}
            />
            <Button
              size="sm"
              variant="outline"
              loading={state.busy === 'query'}
              disabled={state.busy !== null || !state.selectedIndex}
              onClick={() => void runSearch()}
              data-mol-id="storage-search-run"
              style={explorerButton}
            >
              {t('storage.search.run', undefined, { defaultValue: 'Search' })}
            </Button>
          </Flex>
        </>
      )}

      {state.failure && (
        <ProbeFailureNote
          molId="storage-search-note"
          failure={state.failure}
          notConfiguredHint={notConfiguredHint}
        />
      )}

      {state.hits && (
        <div data-mol-id="storage-search-hits">
          <div className={cm.textMuted} style={{ fontSize: 11, marginBottom: 6 }}>
            {t(
              'storage.search.total',
              { count: state.total },
              { defaultValue: `${state.total} total` },
            )}
          </div>
          {state.hits.length === 0 ? (
            <div className={cm.textMuted} style={{ fontSize: 12, marginBottom: 8 }}>
              {t('storage.search.noResults', undefined, { defaultValue: 'No results.' })}
            </div>
          ) : (
            state.hits.map((hit, i) => (
              <pre
                key={i}
                style={{
                  ...EXPLORER_PRE,
                  maxHeight: 200,
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  marginBottom: 6,
                }}
              >
                {JSON.stringify(hit, null, 2)}
              </pre>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// ── Object-storage explorer (dev files + S3) ────────────────────────────────

/** One entry from `GET /projects/:id/files` (the file-explorer endpoint). */
interface UploadDirEntry {
  name: string
  type: 'file' | 'directory'
  /**
   * Byte size when the server reported one. Kept (it used to be discarded) so
   * the browser can refuse to fetch a huge file for a 4 KB text preview
   * instead of pulling megabytes across the network to slice the front off.
   */
  size?: number
}

/** The object-storage row's kept-while-mounted explorer state. */
interface ObjectsRowState {
  /** Whether the lazy on-expand uploads listing has completed. */
  filesLoaded: boolean
  filesBusy: boolean
  /** Subdirectory under `api/uploads` being listed ('' = the uploads root). */
  dir: string
  entries: UploadDirEntry[] | null
  /** True when the files endpoints said there is no running sandbox. */
  filesNotRunning: boolean
  /**
   * True when the server said the app has no `api/` backend at all
   * (`sandbox.error.noApiBackend`) — an EXPECTED state rendered as a quiet
   * hint, distinct from "no files stored yet".
   */
  noApiBackend: boolean
  filesError: string | null
  /** The open file preview (binary/oversized files short-circuit without a fetch). */
  preview: {
    name: string
    /** The file is binary — described, never rendered as decoded text. */
    binary: boolean
    /** The file is too large to text-preview (see FILE_PREVIEW_MAX_BYTES). */
    tooLarge: boolean
    /** Byte size when known, for the "too large"/binary note. */
    size?: number
    loading: boolean
    content: string | null
    truncated: boolean
  } | null
  /** S3 prefix draft. */
  prefix: string
  s3Busy: boolean
  s3: ObjectsListData | null
  s3Failure: StorageProbeFailure | null
}

/** Initial {@link ObjectsRowState}. */
const INITIAL_OBJECTS_STATE: ObjectsRowState = {
  filesLoaded: false,
  filesBusy: false,
  dir: '',
  entries: null,
  filesNotRunning: false,
  noApiBackend: false,
  filesError: null,
  preview: null,
  prefix: '',
  s3Busy: false,
  s3: null,
  s3Failure: null,
}

/**
 * The uploads-dir sentinel the files endpoint resolves server-side to the REAL
 * `<appRoot>/api/uploads` (the app root varies per sandbox — never hardcode an
 * absolute container path here). Missing `api/` backend → 404
 * `sandbox.error.noApiBackend`; api present but uploads dir empty/missing →
 * `{ entries: [] }`.
 */
const UPLOADS_ROOT = '~uploads'

/**
 * The file-storage row's explorer, in two halves:
 *
 *  1. **Dev files browser** (always shown, fired lazily on expand) — lists the
 *     app's `api/uploads` dir through the EXISTING file endpoints
 *     (`GET /projects/:id/files` + `GET /projects/:id/files/<path>`), with
 *     one-level directory navigation and a bounded text preview (obviously
 *     binary extensions short-circuit to a "(binary file)" note).
 *  2. **S3 section** — an on-demand `objects:list` probe ("Browse bucket" +
 *     optional prefix) rendering a key/size/date table.
 *
 * @param root0 - Component props.
 * @param root0.projectId - Project whose files/bucket to explore.
 * @param root0.state - Lifted row state (survives the accordion collapsing).
 * @param root0.patch - Partial-state merger owned by {@link StoragePanel}.
 * @returns The rendered explorer.
 */
function ObjectStorageExplorer({
  projectId,
  state,
  patch,
}: {
  projectId: string
  state: ObjectsRowState
  patch: (p: Partial<ObjectsRowState>) => void
}) {
  const { t } = useTranslation()
  const cm = getClassMap()
  const http = useHttpClient()
  const explorerInput = useExplorerInputStyle()
  const explorerButton = useExplorerButtonStyle()

  const loadFiles = useCallback(
    async (dir: string) => {
      patch({ filesBusy: true, filesError: null, filesNotRunning: false, noApiBackend: false })
      try {
        const res = await http.get<{ path: string; entries: UploadDirEntry[] }>(
          `/projects/${projectId}/files`,
          { params: { path: dir ? `${UPLOADS_ROOT}/${dir}` : UPLOADS_ROOT } },
        )
        const entries = (res.data?.entries ?? [])
          .slice()
          .sort((a, b) =>
            a.type === b.type ? a.name.localeCompare(b.name) : a.type === 'directory' ? -1 : 1,
          )
        patch({ filesBusy: false, filesLoaded: true, dir, entries })
      } catch (error) {
        const key = errorKeyOf(error)
        if (key === 'sandbox.error.noApiBackend') {
          // The app has no api/ backend at all — an expected state (quiet
          // hint), NOT "no files stored yet" and NOT an error.
          patch({ filesBusy: false, filesLoaded: true, noApiBackend: true })
        } else if (key === 'sandbox.error.readDirFailed') {
          // Older servers report a missing uploads dir this way — nothing has
          // been stored (current servers return `{ entries: [] }` instead).
          patch({ filesBusy: false, filesLoaded: true, dir, entries: [] })
        } else if (
          key === 'sandbox.error.none' ||
          key === 'sandbox.error.lost' ||
          key === 'sandbox.error.notFound' ||
          key === 'sandbox.error.noProvider'
        ) {
          patch({ filesBusy: false, filesLoaded: true, filesNotRunning: true })
        } else {
          patch({
            filesBusy: false,
            filesLoaded: true,
            filesError:
              serverErrorMessage(error) ??
              t('storage.files.loadError', undefined, { defaultValue: 'Failed to list files.' }),
          })
        }
      }
    },
    [http, projectId, patch, t],
  )

  // Lazy: fires on first expand only; kept results skip the refetch.
  useEffect(() => {
    if (!state.filesLoaded && !state.filesBusy) void loadFiles(state.dir)
  }, [state.filesLoaded, state.filesBusy, state.dir, loadFiles])

  const openPreview = useCallback(
    async (entry: UploadDirEntry) => {
      const name = entry.name
      const base = { name, size: entry.size, loading: false, content: null, truncated: false }
      // Two cheap short-circuits before any fetch. Neither is the real guard:
      // the server sniffs the CONTENT and answers `{ binary: true }` for
      // everything the extension list misses — which is most uploads, since
      // the filesystem bond stores them under extensionless UUIDs.
      if (isBinaryPreviewPath(name)) {
        patch({ preview: { ...base, binary: true, tooLarge: false } })
        return
      }
      if (typeof entry.size === 'number' && entry.size > FILE_PREVIEW_MAX_BYTES) {
        patch({ preview: { ...base, binary: false, tooLarge: true } })
        return
      }
      patch({
        preview: { ...base, binary: false, tooLarge: false, loading: true },
      })
      const rel = state.dir ? `${state.dir}/${name}` : name
      const encoded = rel.split('/').map(encodeURIComponent).join('/')
      try {
        // The SAME `~uploads` sentinel the listing uses. Hardcoding
        // `api/uploads` here resolved to `/workspace/api/uploads` server-side
        // while the listing resolved `<appRoot>/api/uploads`, so on every
        // scaffolded app the panel listed a file and then insisted it did not
        // exist.
        const res = await http.get<{
          path: string
          content?: string
          binary?: boolean
          size?: number
        }>(`/projects/${projectId}/files/${UPLOADS_ROOT}/${encoded}`)
        if (res.data?.binary) {
          patch({
            preview: { ...base, size: res.data.size ?? entry.size, binary: true, tooLarge: false },
          })
          return
        }
        const content = res.data?.content ?? ''
        patch({
          preview: {
            ...base,
            binary: false,
            tooLarge: false,
            content: content.slice(0, FILE_PREVIEW_CHAR_LIMIT),
            truncated: content.length > FILE_PREVIEW_CHAR_LIMIT,
          },
        })
      } catch (error) {
        patch({
          preview: null,
          filesError:
            serverErrorMessage(error) ??
            t('storage.files.previewError', undefined, { defaultValue: 'Failed to read file.' }),
        })
      }
    },
    [http, projectId, patch, state.dir, t],
  )

  const browseBucket = useCallback(async () => {
    patch({ s3Busy: true, s3Failure: null })
    const prefix = state.prefix.trim()
    const res = await probeStorage<ObjectsListData>(
      http,
      projectId,
      'objects',
      'list',
      prefix ? { prefix } : undefined,
    )
    if (res.ok) patch({ s3Busy: false, s3: res.data })
    else patch({ s3Busy: false, s3: null, s3Failure: res })
  }, [http, projectId, patch, state.prefix])

  const parentDir = state.dir.includes('/') ? state.dir.slice(0, state.dir.lastIndexOf('/')) : ''

  // No api/ backend: the whole explorer (dev files AND S3) is inapplicable —
  // one quiet hint, not an error and not a misleading "No files stored yet".
  if (state.noApiBackend) {
    return (
      <div data-mol-id="storage-objects-explorer">
        <ProbeNote molId="storage-files-note" tone="hint">
          {t('storage.files.noApiBackend', undefined, {
            defaultValue:
              'This app has no API backend yet — file storage activates when one is added.',
          })}
        </ProbeNote>
      </div>
    )
  }

  return (
    <div data-mol-id="storage-objects-explorer">
      {/* ── Dev files browser ── */}
      <Flex align="center" gap="sm" style={{ marginBottom: 8 }}>
        <ExplorerHeading style={{ margin: 0, flex: 1, minWidth: 0 }}>
          {t('storage.files.heading', undefined, { defaultValue: 'Uploaded files (dev)' })}
        </ExplorerHeading>
        <Tooltip content={t('storage.files.refresh', undefined, { defaultValue: 'Refresh' })}>
          <Button
            size="sm"
            variant="outline"
            disabled={state.filesBusy}
            onClick={() => void loadFiles(state.dir)}
            data-mol-id="storage-files-refresh"
            aria-label={t('storage.files.refresh', undefined, { defaultValue: 'Refresh' })}
          >
            <Icon name="rotate" size={14} aria-hidden="true" />
          </Button>
        </Tooltip>
      </Flex>

      {state.filesBusy ? (
        <Flex align="center" justify="center" className={cm.sp('py', 2)}>
          <MoleculeSpinner size="sm" />
        </Flex>
      ) : state.filesNotRunning ? (
        <ProbeNote molId="storage-files-note" tone="warning">
          {t('storage.sandboxNotRunning', undefined, {
            defaultValue: 'The sandbox is not running — start it to explore storage.',
          })}
        </ProbeNote>
      ) : (
        <>
          {state.filesError && (
            <ProbeNote molId="storage-files-note" tone="error">
              {state.filesError}
            </ProbeNote>
          )}
          {state.entries !== null &&
            (state.entries.length === 0 && state.dir === '' ? (
              <div
                data-mol-id="storage-files-empty"
                className={cm.textMuted}
                style={{ fontSize: 12, marginBottom: 10 }}
              >
                {t('storage.files.empty', undefined, { defaultValue: 'No files stored yet.' })}
              </div>
            ) : (
              <div
                data-mol-id="storage-files-list"
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  maxHeight: 200,
                  overflow: 'auto',
                  marginBottom: 10,
                }}
              >
                {/* mol-bespoke-button: a file-browser LIST ROW ("..", up one
                    level), not a CTA — it must match the entry rows beneath it,
                    which a button box would break. */}
                {state.dir !== '' && (
                  <button
                    type="button"
                    data-mol-id="storage-files-up"
                    aria-label={t('storage.files.up', undefined, { defaultValue: 'Up one level' })}
                    onClick={() => void loadFiles(parentDir)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      width: '100%',
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontFamily: 'var(--mol-font-mono, monospace)',
                      color: 'var(--color-foreground-secondary)',
                    }}
                  >
                    <Icon name="folder" size={13} aria-hidden="true" />
                    ..
                  </button>
                )}
                {state.entries.map((entry) => (
                  // mol-bespoke-button: a file-browser LIST ROW (one per
                  // directory/file entry), not a CTA — monospace text rows that
                  // happen to be clickable, never a column of button boxes.
                  <button
                    key={entry.name}
                    type="button"
                    data-mol-id={`storage-file-${entry.name}`}
                    onClick={() =>
                      entry.type === 'directory'
                        ? void loadFiles(state.dir ? `${state.dir}/${entry.name}` : entry.name)
                        : void openPreview(entry)
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      width: '100%',
                      textAlign: 'left',
                      padding: '4px 8px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontFamily: 'var(--mol-font-mono, monospace)',
                      color: 'var(--color-foreground)',
                    }}
                  >
                    <Icon
                      name={entry.type === 'directory' ? 'folder' : 'file'}
                      size={13}
                      aria-hidden="true"
                      className={cm.textSubtle}
                    />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {entry.name}
                      {entry.type === 'directory' ? '/' : ''}
                    </span>
                  </button>
                ))}
                {state.entries.length === 0 && (
                  <div className={cm.textMuted} style={{ padding: '4px 8px', fontSize: 12 }}>
                    {t('storage.files.emptyDir', undefined, { defaultValue: 'Empty directory.' })}
                  </div>
                )}
              </div>
            ))}
        </>
      )}

      {state.preview && (
        <div
          data-mol-id="storage-files-preview"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            marginBottom: 10,
            overflow: 'hidden',
          }}
        >
          <Flex
            align="center"
            gap="sm"
            style={{ padding: '4px 4px 4px 10px', borderBottom: '1px solid var(--color-border)' }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'var(--mol-font-mono, monospace)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {state.preview.name}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => patch({ preview: null })}
              data-mol-id="storage-files-preview-close"
              aria-label={t('storage.files.closePreview', undefined, {
                defaultValue: 'Close preview',
              })}
            >
              <Icon name="x-mark" size={14} aria-hidden="true" />
            </Button>
          </Flex>
          {state.preview.loading ? (
            <Flex align="center" justify="center" className={cm.sp('py', 2)}>
              <MoleculeSpinner size="sm" />
            </Flex>
          ) : state.preview.binary ? (
            <div
              className={cm.textMuted}
              data-mol-id="storage-files-binary"
              style={{ padding: 10, fontSize: 12 }}
            >
              {typeof state.preview.size === 'number'
                ? t(
                    'storage.files.binarySized',
                    { size: formatByteSize(state.preview.size) },
                    {
                      defaultValue: `Binary file (${formatByteSize(state.preview.size)}) — nothing to show here.`,
                    },
                  )
                : t('storage.files.binary', undefined, {
                    defaultValue: 'Binary file — nothing to show here.',
                  })}
            </div>
          ) : state.preview.tooLarge ? (
            <div
              className={cm.textMuted}
              data-mol-id="storage-files-too-large"
              style={{ padding: 10, fontSize: 12 }}
            >
              {t(
                'storage.files.tooLarge',
                { size: formatByteSize(state.preview.size ?? 0) },
                {
                  defaultValue: `Too large to preview (${formatByteSize(state.preview.size ?? 0)}). Open it from the file tree instead.`,
                },
              )}
            </div>
          ) : (
            <>
              <pre style={EXPLORER_PRE}>{state.preview.content ?? ''}</pre>
              {state.preview.truncated && (
                <div className={cm.textMuted} style={{ padding: '0 10px 8px', fontSize: 11 }}>
                  {t(
                    'storage.files.previewTruncated',
                    { count: FILE_PREVIEW_CHAR_LIMIT },
                    {
                      defaultValue: `Preview truncated to the first ${FILE_PREVIEW_CHAR_LIMIT} characters.`,
                    },
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── S3 bucket ── */}
      <ExplorerHeading>
        {t('storage.s3.heading', undefined, { defaultValue: 'S3 bucket' })}
      </ExplorerHeading>
      <Flex align="center" gap="sm" style={{ marginBottom: 8 }}>
        <input
          type="text"
          data-mol-id="storage-s3-prefix"
          value={state.prefix}
          onChange={(e) => patch({ prefix: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !state.s3Busy) void browseBucket()
          }}
          placeholder={t('storage.s3.prefixPlaceholder', undefined, {
            defaultValue: 'Prefix (optional)',
          })}
          spellCheck={false}
          style={explorerInput}
        />
        <Button
          size="sm"
          variant="outline"
          loading={state.s3Busy}
          disabled={state.s3Busy}
          onClick={() => void browseBucket()}
          data-mol-id="storage-s3-browse"
          style={explorerButton}
        >
          {t('storage.s3.browse', undefined, { defaultValue: 'Browse bucket' })}
        </Button>
      </Flex>

      {state.s3Failure && (
        <ProbeFailureNote
          molId="storage-s3-note"
          failure={state.s3Failure}
          notConfiguredHint={t('storage.s3.notConfigured', undefined, {
            defaultValue: 'Add S3 credentials above to browse your bucket.',
          })}
        />
      )}

      {state.s3 && (
        <div data-mol-id="storage-s3-list">
          <div
            className={cm.textMuted}
            style={{
              fontSize: 11,
              fontFamily: 'var(--mol-font-mono, monospace)',
              marginBottom: 6,
            }}
          >
            {t(
              'storage.s3.summary',
              { bucket: state.s3.bucket, count: state.s3.objects.length },
              {
                defaultValue: `${state.s3.bucket} · ${state.s3.objects.length} object${
                  state.s3.objects.length === 1 ? '' : 's'
                }`,
              },
            )}
            {state.s3.truncated
              ? ` · ${t('storage.s3.truncated', undefined, {
                  defaultValue: 'showing the first 100',
                })}`
              : ''}
          </div>
          {state.s3.objects.length === 0 ? (
            <div
              data-mol-id="storage-s3-empty"
              className={cm.textMuted}
              style={{ fontSize: 12, marginBottom: 8 }}
            >
              {t('storage.s3.empty', undefined, { defaultValue: 'No objects found.' })}
            </div>
          ) : (
            <div style={{ overflow: 'auto', maxHeight: 240, marginBottom: 8 }}>
              <table
                style={{
                  borderCollapse: 'collapse',
                  fontSize: 12,
                  fontFamily: 'var(--mol-font-mono, monospace)',
                  minWidth: '100%',
                }}
              >
                <thead>
                  <tr>
                    {(
                      [
                        ['key', t('storage.s3.colKey', undefined, { defaultValue: 'Key' })],
                        ['size', t('storage.s3.colSize', undefined, { defaultValue: 'Size' })],
                        [
                          'modified',
                          t('storage.s3.colModified', undefined, { defaultValue: 'Modified' }),
                        ],
                      ] as const
                    ).map(([id, label]) => (
                      <th
                        key={id}
                        style={{
                          textAlign: 'left',
                          padding: '5px 12px',
                          borderBottom: '1px solid var(--color-border)',
                          color: 'var(--color-foreground-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                          background: 'var(--color-surface)',
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {state.s3.objects.map((object, rowIdx) => (
                    <tr
                      key={object.key}
                      data-mol-id={`storage-s3-row-${rowIdx}`}
                      className={cm.cn(
                        cm.tableRowHoverable,
                        rowIdx % 2 === 1 && cm.tableRowStriped,
                      )}
                    >
                      <td
                        style={{
                          padding: '4px 12px',
                          borderBottom: '1px solid var(--color-border)',
                          maxWidth: 360,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {object.key}
                      </td>
                      <td
                        style={{
                          padding: '4px 12px',
                          borderBottom: '1px solid var(--color-border)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatByteSize(object.size)}
                      </td>
                      <td
                        style={{
                          padding: '4px 12px',
                          borderBottom: '1px solid var(--color-border)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {object.lastModified ? new Date(object.lastModified).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── The panel ────────────────────────────────────────────────────────────────

/** Props for {@link StoragePanel}. */
interface StoragePanelProps {
  /** Project whose storage to configure and explore. */
  projectId: string
}

/**
 * The Storage panel — see the module doc above. Polls `/setup` AND the live
 * `/services` state every 10s (visibility-aware, same pattern as
 * EnvironmentPanel). `/setup` only returns packages that declare secrets, so
 * the zero-config providers molecule handles automatically (memory cache,
 * filesystem uploads, Postgres-backed search) are invisible to it — the
 * `/services` response's live `packages` list is what marks those types
 * present ("In your app" + "Auto-configured"). Draft input values live in
 * separate state so a poll never clobbers in-progress typing.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose storage to configure and explore.
 * @returns The rendered panel.
 */
export function StoragePanel({ projectId }: StoragePanelProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()

  const [integrations, setIntegrations] = useState<SetupIntegration[]>([])
  // The LIVE installed @molecule/* list from GET /services — sees the
  // zero-config providers /setup can't (they declare no secrets).
  const [servicesPackages, setServicesPackages] = useState<string[]>([])
  /**
   * Whether THIS deployment provisions managed object storage (`/setup` →
   * `platform.managedObjectStorage`). Feature detection, not an assumption:
   * where it is false there is no molecule default for file storage to be "on
   * standby", and the row must not claim one.
   */
  const [managedObjectStorage, setManagedObjectStorage] = useState(false)
  /**
   * The caller's access to this panel's data, learned from the routes' own
   * 403s. Storage exploration is admin+ and `/setup` + `/services` are
   * editor+, so a viewer or editor on a shared project hits a DELIBERATE
   * denial — which the panel used to swallow into `console.error`, leaving
   * `integrations` and `packages` empty and rendering confident, false copy
   * ("This app has no API backend yet") about a project that plainly has one.
   */
  const [access, setAccess] = useState<'ok' | 'restricted'>('ok')
  /**
   * A save that was refused because the owner is a guest. The vault needs a
   * registered account to own credentials, so the answer is a signup path —
   * not the generic "check your access and try again" toast that left an
   * anonymous user at a dead end with their typed keys still in the inputs.
   */
  const [requiresAccount, setRequiresAccount] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // Single-open accordion across the whole view (one storage type at a time).
  const [expanded, setExpanded] = useState<string | null>(null)
  // Secret drafts, keyed by secret name — separate from fetched status so the
  // poll never clobbers in-progress input.
  const [values, setValues] = useState<Record<string, string>>({})
  const [savingSetup, setSavingSetup] = useState(false)
  // The per-type "Use your own provider" switch. PRESENTATIONAL: on reveals
  // the credential inputs (or the ask-Synthase line when no credentialed
  // provider is installed); off shows only the managed line — it never
  // deletes anything. Absent entries derive from state: on iff a user-set key
  // exists (missing required keys also derive it on — the setup flow always
  // leads with its inputs).
  const [ownSwitch, setOwnSwitch] = useState<Partial<Record<StorageType, boolean>>>({})

  // Explorer state lives HERE (not in the explorer components) because EnvRow
  // unmounts its body on collapse — lifting it keeps each row's results while
  // the panel stays mounted, and makes the on-expand loads fire only once.
  const [cacheState, setCacheState] = useState<CacheRowState>(INITIAL_CACHE_STATE)
  const [searchState, setSearchState] = useState<SearchRowState>(INITIAL_SEARCH_STATE)
  const [objectsState, setObjectsState] = useState<ObjectsRowState>(INITIAL_OBJECTS_STATE)
  const patchCache = useCallback(
    (p: Partial<CacheRowState>) => setCacheState((prev) => ({ ...prev, ...p })),
    [],
  )
  const patchSearch = useCallback(
    (p: Partial<SearchRowState>) => setSearchState((prev) => ({ ...prev, ...p })),
    [],
  )
  const patchObjects = useCallback(
    (p: Partial<ObjectsRowState>) => setObjectsState((prev) => ({ ...prev, ...p })),
    [],
  )

  const loadSetup = useCallback(async () => {
    try {
      const res = await http.get<SetupResponse>(`/projects/${projectId}/setup`)
      setIntegrations(res.data?.integrations ?? [])
      setManagedObjectStorage(res.data?.platform?.managedObjectStorage === true)
      setAccess('ok')
    } catch (error) {
      if (isInsufficientRoleError(error)) {
        // A deliberate denial, not a failure: keep the panel honest about WHY
        // it has nothing to show instead of describing the project wrongly.
        setAccess('restricted')
      } else {
        console.error('StoragePanel: failed to load setup status', error)
      }
    }
    setLoaded(true)
  }, [http, projectId])

  const loadServices = useCallback(
    async (opts: { refresh?: boolean } = {}) => {
      try {
        const res = await loadServicesState(http, projectId, opts)
        if (res) setServicesPackages(res.packages)
      } catch (error) {
        if (isInsufficientRoleError(error)) {
          setAccess('restricted')
        } else {
          // Non-fatal: presence falls back to the credentialed /setup
          // integrations (zero-config providers just won't be marked in-app
          // until it recovers).
          console.error('StoragePanel: failed to load services state', error)
        }
      }
    },
    [http, projectId],
  )

  useEffect(() => {
    void loadSetup()
    // Opening the panel is the one moment worth a real sandbox re-scan; the
    // poll below rides the server's TTL instead, because that scan is five
    // remote round trips into the user's own microVM.
    void loadServices({ refresh: true })
    // Live refresh: integrations AND installed packages can change from
    // OUTSIDE this panel (Synthase installing a cache/search/uploads package
    // mid-session). Drafts live in separate state, so a refresh never clobbers
    // typing. Hidden tabs skip.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadSetup()
        void loadServices()
      }
    }, 10_000)
    return () => clearInterval(interval)
  }, [loadSetup, loadServices])

  const handleSaveSetup = useCallback(
    async (integration: SetupIntegration) => {
      setSavingSetup(true)
      try {
        const saved = await saveIntegrationSecrets(
          http,
          projectId,
          integration,
          values,
          t,
          'all',
          // A guest owner CAN'T save: the vault needs a registered account. Show
          // the signup path rather than a dead-end toast, and keep the typed
          // values so signing up does not cost the user their input.
          (kind) =>
            kind === 'requiresSignup' ? setRequiresAccount(true) : setAccess('restricted'),
        )
        if (saved) {
          setValues((prev) => {
            const next = { ...prev }
            for (const key of saved) delete next[key]
            return next
          })
          await loadSetup()
        }
      } finally {
        setSavingSetup(false)
      }
    },
    [http, projectId, values, loadSetup, t],
  )

  const setSecretValue = useCallback(
    (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value })),
    [],
  )

  // ── Derived view model ─────────────────────────────────────────────────────

  // Same integration-level dedup as the Environment panel (identical secret
  // sets collapse into one), so the two views never disagree about a package.
  const dedupedIntegrations = useMemo(() => dedupeIntegrations(integrations), [integrations])

  /** The installed integrations belonging to a storage type. */
  const integrationsByType = useMemo(() => {
    const map = new Map<StorageType, SetupIntegration[]>()
    for (const entry of STORAGE_CATALOG) {
      map.set(
        entry.type,
        dedupedIntegrations.filter(
          (i) => i.category !== undefined && entry.registryCategories.includes(i.category),
        ),
      )
    }
    return map
  }, [dedupedIntegrations])

  /**
   * Types whose zero-config packages appear in the LIVE installed list —
   * presence `/setup` cannot see (those providers declare no secrets).
   */
  const packagesByType = useMemo(() => {
    const map = new Map<StorageType, string[]>()
    for (const entry of STORAGE_CATALOG) {
      map.set(
        entry.type,
        servicesPackages.filter((pkg) =>
          entry.packagePrefixes.some((prefix) => pkg.startsWith(prefix)),
        ),
      )
    }
    return map
  }, [servicesPackages])

  /**
   * Whether the app has an api backend at all (any live `@molecule/api-*`
   * package). Drives the file-storage platform default: with a backend,
   * local-disk storage works with nothing to set up.
   */
  const hasApiBackend = useMemo(
    () => servicesPackages.some((pkg) => pkg.startsWith('@molecule/api-')),
    [servicesPackages],
  )

  /** The cache/search providers the app installed — drive every provider-specific string. */
  const cacheProvider = useMemo(
    () => providerFromPackages(servicesPackages, CACHE_PROVIDER_PACKAGES),
    [servicesPackages],
  )
  const searchProvider = useMemo(
    () => providerFromPackages(servicesPackages, SEARCH_PROVIDER_PACKAGES),
    [servicesPackages],
  )

  // "In your app" = types with an installed matching integration OR any live
  // installed package matching the type's prefixes, PLUS the database always —
  // molecule provisions Postgres for every project — PLUS file storage
  // whenever the app has an api backend (local-disk uploads are the platform
  // default; no provider package needed).
  const groups = useMemo(() => {
    const inApp: StorageCatalogEntry[] = []
    const more: StorageCatalogEntry[] = []
    for (const entry of STORAGE_CATALOG) {
      const matched = integrationsByType.get(entry.type) ?? []
      const livePackages = packagesByType.get(entry.type) ?? []
      if (
        entry.type === 'database' ||
        matched.length > 0 ||
        livePackages.length > 0 ||
        (entry.type === 'object-storage' && hasApiBackend)
      ) {
        inApp.push(entry)
      } else {
        more.push(entry)
      }
    }
    return { inApp, more }
  }, [integrationsByType, packagesByType, hasApiBackend])

  /**
   * Whether the user's own keys drive this type (a set secret the USER
   * saved — molecule's broker and managed vault rows never count).
   */
  const userConfigured = (matched: SetupIntegration[]): boolean =>
    matched.some((i) => i.secrets.some(isUserSetSecret))

  /**
   * The molecule-managed default-provider variant for a type: managed vault
   * rows win (molecule object storage), then an installed provider package
   * (checked against BOTH the live `/services` package list and the matched
   * `/setup` integrations, so a slow/failed live fetch can't hide it), then
   * an unconditional variant (the database's managed Postgres).
   */
  const managedDefaultFor = (
    entry: StorageCatalogEntry,
    matched: SetupIntegration[],
  ): ManagedDefaultVariant | null => {
    const livePackages = packagesByType.get(entry.type) ?? []
    for (const variant of MOLECULE_DEFAULT_PROVIDERS[entry.type]) {
      // A variant that claims a molecule-provided default is only selectable
      // where the platform actually provides it.
      if (variant.managedStorage && !managedObjectStorage) continue
      // A platform-default variant describes the ABSENCE of a choice.
      if (variant.noProviderPackage && (livePackages.length > 0 || matched.length > 0)) continue
      if (variant.managedVault) {
        if (matched.some((i) => i.secrets.some((s) => s.vaultSource === 'managed'))) return variant
        continue
      }
      const prefix = variant.packagePrefix
      if (prefix) {
        if (
          servicesPackages.some((pkg) => pkg.startsWith(prefix)) ||
          matched.some((i) => i.package.startsWith(prefix))
        ) {
          return variant
        }
        continue
      }
      if (variant.apiBackend) {
        if (hasApiBackend) return variant
        continue
      }
      return variant
    }
    return null
  }

  // ── Chips (the UNIFIED in-app precedence, shared with the Environment
  //    panel's rows — identical labels + tones) ───────────────────────────────

  /**
   * Chip for a storage-type row. In-app precedence: missing required keys warn
   * first, then a user-set key ("Your keys"), and everything molecule already
   * handles reads "Auto-configured" — the "provisioned by molecule" prose
   * lives in the expanded details, not the summary pill. Discovery rows read
   * "Available" — not an error state: every storage type here can be added
   * from chat, free in dev.
   */
  const chipFor = (entry: StorageCatalogEntry, matched: SetupIntegration[], inApp: boolean) => {
    const secrets = matched.flatMap((i) => i.secrets)
    const missingCount = secrets.filter((s) => s.required && s.status === 'missing').length
    if (missingCount > 0) {
      return (
        <Chip tone="warning" molId={`storage-chip-${entry.type}`}>
          {t(
            'workspace.setup.status.needsSetupCount',
            { count: missingCount },
            { defaultValue: `${missingCount} key${missingCount === 1 ? '' : 's'} needed` },
          )}
        </Chip>
      )
    }
    if (secrets.some(isUserSetSecret)) {
      return (
        <Chip tone="byo" molId={`storage-chip-${entry.type}`}>
          {t('environment.chip.yourKeys', undefined, { defaultValue: 'Your keys' })}
        </Chip>
      )
    }
    if (inApp) {
      return (
        <Chip tone="managed" molId={`storage-chip-${entry.type}`}>
          {t('workspace.setup.status.managed', undefined, { defaultValue: 'Auto-configured' })}
        </Chip>
      )
    }
    return (
      <Chip tone="none" molId={`storage-chip-${entry.type}`}>
        {t('storage.chip.available', undefined, { defaultValue: 'Available' })}
      </Chip>
    )
  }

  // ── Rows ───────────────────────────────────────────────────────────────────

  const toggleRow = (key: string) => setExpanded((cur) => (cur === key ? null : key))

  /** The expanded-row description line (both sections). */
  const description = (entry: StorageCatalogEntry): ReactNode => (
    <p
      style={{
        margin: '0 0 10px',
        fontSize: 12,
        color: 'var(--color-foreground-secondary)',
      }}
    >
      {t(`storage.type.${entry.type}.desc`, undefined, { defaultValue: entry.description })}
    </p>
  )

  const renderRow = (entry: StorageCatalogEntry, inApp: boolean) => {
    const matched = integrationsByType.get(entry.type) ?? []
    const livePackages = packagesByType.get(entry.type) ?? []
    const isOpen = expanded === entry.type
    const isUserConfigured = userConfigured(matched)
    const hasCredentialedProvider = matched.some((i) => i.secrets.length > 0)
    const rowSecrets = matched.flatMap((i) => i.secrets)
    const hasMissingRequired = rowSecrets.some((s) => s.required && s.status === 'missing')
    const managedDefault = managedDefaultFor(entry, matched)
    const managedLabel = managedDefault
      ? t(`storage.defaultProvider.${managedDefault.key}`, undefined, {
          defaultValue: managedDefault.label,
        })
      : null
    // The row subtitle names what's installed: credentialed integrations
    // first, else the zero-config package(s) the live list sees.
    const subtitle =
      matched.length > 0
        ? matched.map((i) => i.package).join(', ')
        : livePackages.length > 0
          ? livePackages.join(', ')
          : undefined
    // The switch renders ONLY where a molecule default AND a user alternative
    // both exist: in-app, non-database rows with a managed default — an
    // installed zero-config/in-container provider OR managed vault rows
    // (molecule object storage). (The database has no alternative; a row
    // without a managed default has nothing to fall back to, so its inputs
    // always show.)
    // …and only when that default is MOLECULE'S. A `byo` variant describes the
    // user's own provider, so there is nothing to switch back to; offering the
    // switch there is what rendered "Molecule's default is on standby" beside
    // a default that does not exist on this deployment.
    const showSwitch =
      inApp && entry.type !== 'database' && managedDefault !== null && !managedDefault.byo
    // PRESENTATIONAL: on iff the user toggled it on, or (untouched) a
    // user-set key exists / required keys are missing — the setup flow always
    // leads with its inputs. Turning it off never deletes anything.
    const ownOn = showSwitch && (ownSwitch[entry.type] ?? (isUserConfigured || hasMissingRequired))
    // Credential rows show while "Use your own provider" is on; without a
    // switch they ARE the configure surface — except a fully-managed secret
    // set (e.g. the provisioned DATABASE_URL), which has nothing to configure.
    const showInputs = matched.length > 0 && (showSwitch ? ownOn : !allSecretsManaged(rowSecrets))
    return (
      <EnvRow
        key={entry.type}
        molId={`storage-row-${entry.type}`}
        icon={entry.icon}
        label={t(`storage.type.${entry.type}`, undefined, { defaultValue: entry.label })}
        packageName={subtitle}
        chip={chipFor(entry, matched, inApp)}
        expanded={isOpen}
        onToggle={() => toggleRow(entry.type)}
      >
        {/* 1 — description */}
        {description(entry)}

        {/* 2 — ONE explanatory muted line: the managed story while molecule's
            default handles the type (merged default label + explainer; the
            summary chip just says "Auto-configured"). With saved keys and the
            switch off, the muted remove-keys line rides under it — turning
            the switch off never deletes anything. */}
        {inApp && entry.type === 'database' && managedLabel && (
          <MutedNote molId="storage-managed-database">
            {t(
              'storage.managed.default',
              { label: managedLabel },
              { defaultValue: `Molecule default: ${managedLabel}` },
            )}
          </MutedNote>
        )}
        {/* A user's-own provider gets the SAME single muted line, minus the
            switch — it is an explanation, not a choice. */}
        {inApp && entry.type !== 'database' && managedDefault?.byo && (
          <MutedNote molId={`storage-managed-${entry.type}`}>
            {t(`storage.managed.${managedDefault.key}`, undefined, {
              defaultValue: managedDefault.explainer,
            })}
          </MutedNote>
        )}
        {/* 2+3 — one SETTINGS ROW: the explanatory line (left) and the switch
            (right) share a line, matching the Environment panel's idiom. */}
        {showSwitch && managedDefault && (
          <Flex align="center" justify="between" gap="sm" style={{ marginBottom: 10 }}>
            <MutedNote molId={`storage-managed-${entry.type}`} style={{ margin: 0, flex: 1 }}>
              {ownOn
                ? t('storage.ownProviderHint', undefined, {
                    defaultValue: "Using your own provider — Molecule's default is on standby.",
                  })
                : t(`storage.managed.${managedDefault.key}`, undefined, {
                    defaultValue: managedDefault.explainer,
                  })}
            </MutedNote>
            <Switch
              data-mol-id={`storage-own-switch-${entry.type}`}
              label={t('storage.useYourOwnProvider', undefined, {
                defaultValue: 'Use your own provider',
              })}
              checked={ownOn}
              size="sm"
              onChange={() => setOwnSwitch((prev) => ({ ...prev, [entry.type]: !ownOn }))}
            />
          </Flex>
        )}
        {showSwitch && !ownOn && isUserConfigured && (
          <MutedNote molId={`storage-fallback-hint-${entry.type}`}>
            {t('storage.toggle.removeKeysHint', undefined, {
              defaultValue:
                "Your keys are saved — turn the switch back on to view or remove them; Molecule's managed defaults restore automatically after removal.",
            })}
          </MutedNote>
        )}

        {/* 4 — content: with the switch on but no credentialed provider
            installed, point at Synthase (it installs and wires one from chat) */}
        {showSwitch && ownOn && !hasCredentialedProvider && (
          <MutedNote molId={`storage-add-hint-${entry.type}`}>
            {t(
              'storage.toggle.askSynthase',
              { example: SWAP_EXAMPLES[entry.type as Exclude<StorageType, 'database'>] },
              {
                defaultValue: `Ask Synthase in chat to switch providers — e.g. "${SWAP_EXAMPLES[entry.type as Exclude<StorageType, 'database'>]}" — it installs and wires everything.`,
              },
            )}
          </MutedNote>
        )}

        {/* 4 — content: every matching integration's rich credential rows —
            the SAME rendering (broker/provisioned/auto notes, masked values,
            right-aligned Save) the Environment panel uses. */}
        {showInputs &&
          matched.map((integration) => (
            <div key={integration.package} style={{ marginBottom: 10 }}>
              <IntegrationSecrets
                integration={integration}
                values={values}
                onValueChange={setSecretValue}
                saving={savingSetup}
                onSave={handleSaveSetup}
                showHeading
                override={showSwitch && ownOn}
              />
            </div>
          ))}

        {/* Explore: each type embeds a SIMPLE, read-only explorer beneath its
            credential config. The database row embeds the FULL explorer; the
            others embed a lazy probe/files explorer (only mounts — and only
            fires — while the row is open, since EnvRow unmounts its body on
            collapse; results are kept in the panel's state across collapses).
            These replace the old "add it in chat" placeholder — each explorer
            surfaces its own storage-specific notConfigured guidance. */}
        {isOpen && entry.type === 'database' && <DatabasePanel projectId={projectId} embedded />}
        {isOpen && entry.type === 'object-storage' && inApp && (
          <ObjectStorageExplorer projectId={projectId} state={objectsState} patch={patchObjects} />
        )}
        {isOpen && entry.type === 'cache' && inApp && (
          <CacheExplorer
            projectId={projectId}
            provider={cacheProvider}
            state={cacheState}
            patch={patchCache}
          />
        )}
        {isOpen && entry.type === 'search' && inApp && (
          <SearchExplorer
            projectId={projectId}
            provider={searchProvider}
            state={searchState}
            patch={patchSearch}
          />
        )}

        {/* Discovery rows have nothing to explore YET — not an error state:
            file storage without an api backend gets the quiet no-backend hint;
            cache/search point at Synthase (free in dev), the same idiom as the
            provider-swap hint above. */}
        {isOpen && !inApp && entry.type === 'object-storage' && (
          <MutedNote molId="storage-discovery-hint-object-storage">
            {t('storage.files.noApiBackend', undefined, {
              defaultValue:
                'This app has no API backend yet — file storage activates when one is added.',
            })}
          </MutedNote>
        )}
        {isOpen && !inApp && entry.type === 'cache' && (
          <MutedNote molId="storage-discovery-hint-cache">
            {t('storage.cache.addHint', undefined, {
              defaultValue: 'Ask Synthase in chat to add caching — free in dev.',
            })}
          </MutedNote>
        )}
        {isOpen && !inApp && entry.type === 'search' && (
          <MutedNote molId="storage-discovery-hint-search">
            {t('storage.search.addHint', undefined, {
              defaultValue: 'Ask Synthase in chat to add search — free in dev.',
            })}
          </MutedNote>
        )}
      </EnvRow>
    )
  }

  return (
    <div data-mol-id="storage-panel" style={{ padding: 16, fontSize: 13 }}>
      {/* Panel-level access notices lead the contents, matching the
          Environment panel's idiom. */}
      {requiresAccount && (
        <PanelNotice
          tone="primary"
          molId="storage-guest-note"
          action={
            // A real anchor (styled as the solid primary sm button) so the
            // IDE's auth-modal click interceptor opens the in-IDE signup —
            // a programmatic navigate() escapes it.
            <a
              href="/signup"
              data-mol-id="storage-guest-signup"
              className={cm.button({ variant: 'solid', color: 'primary', size: 'sm' })}
              style={{ textDecoration: 'none' }}
            >
              {t('environment.guestSignup', undefined, { defaultValue: 'Sign up free' })}
            </a>
          }
        >
          {t('storage.guestNote', undefined, {
            defaultValue:
              "You're browsing as a guest. Sign up free to save credentials — your typed values are kept.",
          })}
        </PanelNotice>
      )}
      {access === 'restricted' && (
        <PanelNotice tone="info" molId="storage-restricted-note">
          {t('storage.restrictedNote', undefined, {
            defaultValue:
              'You have read access to this project. Storage details need an editor or admin role.',
          })}
        </PanelNotice>
      )}
      {!loaded ? (
        <Flex align="center" justify="center" className={cm.sp('py', 6)}>
          <MoleculeSpinner />
        </Flex>
      ) : access === 'restricted' ? (
        // With /setup and /services both denied there is NOTHING true to say
        // about this project's storage. The panel used to render its full
        // catalog off empty data, asserting "This app has no API backend yet"
        // and "ask Synthase to add caching" for a project that plainly had
        // both. Only the database row survives, because it is member-readable.
        <>
          <SectionHeading molId="storage-group-in-your-app" first>
            {t('storage.group.inYourApp', undefined, { defaultValue: 'In your app' })}
          </SectionHeading>
          {renderRow(STORAGE_CATALOG[0], true)}
        </>
      ) : (
        <>
          {/* Section 1 — storage the app actually uses (database always) */}
          <SectionHeading molId="storage-group-in-your-app" first>
            {t('storage.group.inYourApp', undefined, { defaultValue: 'In your app' })}
          </SectionHeading>
          {groups.inApp.map((entry) => renderRow(entry, true))}

          {/* Section 2 — the rest of the storage types (the discovery surface) */}
          {groups.more.length > 0 && (
            <>
              <SectionHeading molId="storage-group-more">
                {t('storage.group.more', undefined, { defaultValue: 'More storage types' })}
              </SectionHeading>
              {groups.more.map((entry) => renderRow(entry, false))}
            </>
          )}
        </>
      )}
    </div>
  )
}
